import { useCallback, useMemo, useState } from 'react'
import { apiService } from '@/services/api'
import { formatClientDisplayName } from '../utils/sessionDetailHelpers'

// Shared same/different-client -> program (-> optional secondary selection) copy flow,
// used by the four "copy to another client/program" dialogs (nutrition plan copy, nutrition
// global copy, meal copy, exercise plan copy). Each call site supplies its own `validate` and
// `buildRequests` closures to express its own payload shape; the fetch-clients/fetch-programs
// mechanics (byte-identical across all four before this extraction) live here once.
export const useClientProgramCopy = ({
  companyUrl,
  user,
  defaultClientId,
  onSaveSuccess,
  extraSteps, // optional: { onProgramSelected: async (programId, ctx) => secondaryOptions[] }
  validate, // (ctx) => string | null
  buildRequests, // (ctx) => { endpoint, payload } | [{ endpoint, payload }, ...]
}) => {
  const [dialog, setDialog] = useState(null)
  const [targetType, setTargetType] = useState('same')
  const [clients, setClients] = useState([])
  const [clientsLoading, setClientsLoading] = useState(false)
  const [clientsError, setClientsError] = useState('')
  const [clientSearch, setClientSearch] = useState('')
  const [clientMenuOpen, setClientMenuOpen] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [programs, setPrograms] = useState([])
  const [programsLoading, setProgramsLoading] = useState(false)
  const [programsError, setProgramsError] = useState('')
  const [programId, setProgramId] = useState('')
  const [secondaryOptions, setSecondaryOptions] = useState([])
  const [secondaryLoading, setSecondaryLoading] = useState(false)
  const [secondarySelectedId, setSecondarySelectedId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchPrograms = useCallback(
    async (targetClientId) => {
      if (!companyUrl || !targetClientId) return
      setProgramsLoading(true)
      setProgramsError('')
      try {
        const res = await apiService.post(`/${companyUrl}/getclientprograms`, { client_id: String(targetClientId) })
        if (!res.success) {
          throw new Error(res.error || 'Unable to load programs.')
        }
        const list = res.data?.programs || res.data?.data || res.data || []
        setPrograms(Array.isArray(list) ? list : [])
      } catch (err) {
        setProgramsError(err?.message || 'Unable to load programs.')
      } finally {
        setProgramsLoading(false)
      }
    },
    [companyUrl]
  )

  const fetchClients = useCallback(async () => {
    if (!companyUrl) return
    const consultantId = user?.id || user?.userId
    if (!consultantId) return
    setClientsLoading(true)
    setClientsError('')
    try {
      const res = await apiService.post(`/${companyUrl}/fetchloggedinconsultantclients`, {
        client_id: String(consultantId),
      })
      if (!res.success) {
        throw new Error(res.error || 'Unable to load clients.')
      }
      const list = Array.isArray(res.data) ? res.data : res.data?.clients || res.data?.data || []
      setClients(Array.isArray(list) ? list : [])
    } catch (err) {
      setClientsError(err?.message || 'Unable to load clients.')
    } finally {
      setClientsLoading(false)
    }
  }, [companyUrl, user])

  const resetPickerState = useCallback(() => {
    setTargetType('same')
    setPrograms([])
    setProgramsError('')
    setProgramId('')
    setError('')
    setClients([])
    setClientsError('')
    setClientSearch('')
    setClientMenuOpen(false)
    setSelectedClient(null)
    setSecondaryOptions([])
    setSecondarySelectedId('')
  }, [])

  const open = useCallback(
    async (seed = null) => {
      setDialog(seed)
      resetPickerState()
      if (defaultClientId) {
        await fetchPrograms(defaultClientId)
      }
    },
    [defaultClientId, fetchPrograms, resetPickerState]
  )

  const close = useCallback(() => {
    if (saving) return
    setDialog(null)
    resetPickerState()
  }, [saving, resetPickerState])

  const selectTargetType = useCallback(
    async (nextType) => {
      setTargetType(nextType)
      setProgramId('')
      setError('')
      setSecondaryOptions([])
      setSecondarySelectedId('')
      if (nextType === 'same') {
        if (defaultClientId) await fetchPrograms(defaultClientId)
      } else {
        setPrograms([])
        setProgramsError('')
        setSelectedClient(null)
        setClientSearch('')
        fetchClients()
      }
    },
    [defaultClientId, fetchClients, fetchPrograms]
  )

  const selectClient = useCallback(
    async (client) => {
      setSelectedClient(client)
      setClientSearch(formatClientDisplayName(client))
      setClientMenuOpen(false)
      setProgramId('')
      setPrograms([])
      setError('')
      setSecondaryOptions([])
      setSecondarySelectedId('')
      await fetchPrograms(client.id)
    },
    [fetchPrograms]
  )

  const clearClient = useCallback(() => {
    setSelectedClient(null)
    setClientSearch('')
    setProgramId('')
    setPrograms([])
    setSecondaryOptions([])
    setSecondarySelectedId('')
  }, [])

  const selectProgram = useCallback(
    async (nextProgramId) => {
      setProgramId(String(nextProgramId))
      setSecondarySelectedId('')
      setSecondaryOptions([])
      if (!extraSteps?.onProgramSelected) return
      setSecondaryLoading(true)
      try {
        const targetClientId = targetType === 'same' ? defaultClientId : selectedClient?.id
        const options = await extraSteps.onProgramSelected(nextProgramId, { targetClientId })
        setSecondaryOptions(Array.isArray(options) ? options : [])
      } catch (err) {
        setError(err?.message || 'Unable to load options.')
      } finally {
        setSecondaryLoading(false)
      }
    },
    [defaultClientId, extraSteps, selectedClient, targetType]
  )

  const save = useCallback(async () => {
    const ctx = { dialog, targetType, selectedClient, programId, secondarySelectedId }
    const validationError = validate ? validate(ctx) : null
    if (validationError) {
      setError(validationError)
      return
    }
    setSaving(true)
    setError('')
    try {
      const requests = buildRequests(ctx)
      const list = Array.isArray(requests) ? requests : [requests]
      for (const { endpoint, payload } of list) {
        const res = await apiService.post(`/${companyUrl}/${endpoint}`, payload)
        if (!res.success) {
          throw new Error(res.error || 'Unable to complete copy.')
        }
      }
      close()
      if (onSaveSuccess) await onSaveSuccess()
    } catch (err) {
      setError(err?.message || 'Unable to complete copy.')
    } finally {
      setSaving(false)
    }
  }, [dialog, targetType, selectedClient, programId, secondarySelectedId, validate, buildRequests, companyUrl, close, onSaveSuccess])

  const filteredClients = useMemo(() => {
    const term = clientSearch.trim().toLowerCase()
    if (!term) return clients
    return clients.filter((client) => {
      const name = formatClientDisplayName(client).toLowerCase()
      const email = (client?.email || '').toLowerCase()
      return name.includes(term) || email.includes(term)
    })
  }, [clientSearch, clients])

  return {
    dialog,
    open,
    close,
    targetType,
    selectTargetType,
    clients,
    filteredClients,
    clientsLoading,
    clientsError,
    clientSearch,
    setClientSearch,
    clientMenuOpen,
    setClientMenuOpen,
    selectedClient,
    selectClient,
    clearClient,
    programs,
    programsLoading,
    programsError,
    programId,
    selectProgram,
    secondaryOptions,
    secondaryLoading,
    secondarySelectedId,
    setSecondarySelectedId,
    saving,
    error,
    save,
  }
}
